import { Card } from "@/components/ui/Card";
import { OnlineBookingMinStayPeriodsEditor } from "@/components/onlineBooking/OnlineBookingMinStayPeriodsEditor";
import { OnlineBookingSeasonsEditor } from "@/components/onlineBooking/OnlineBookingSeasonsEditor";
import { OnlineBookingFormHint } from "@/pages/stammdaten/online-booking/OnlineBookingFormHint";
import type { OnlineBookingRegelnSub } from "@/pages/stammdaten/online-booking/onlineBookingSubSections";
import type { OnlineBookingSettings } from "@/pages/stammdaten/online-booking/useOnlineBookingSettings";

type Props = Pick<
  OnlineBookingSettings,
  "seasons" | "setSeasons" | "minStay" | "setMinStay" | "sameDayCutoff" | "setSameDayCutoff"
> & {
  canWrite: boolean;
  subSection: OnlineBookingRegelnSub;
};

export function OnlineBookingRegelnPanel({
  canWrite,
  subSection,
  seasons,
  setSeasons,
  minStay,
  setMinStay,
  sameDayCutoff,
  setSameDayCutoff,
}: Props) {
  if (subSection === "saisons") {
    return (
      <Card
        title="Buchungssaisons"
        description="Steuert, in welchen Zeiträumen Gäste online buchen dürfen. Außerhalb ist die Buchung gesperrt."
        bodyClassName="space-y-4"
      >
        <OnlineBookingFormHint>
          Ohne Einträge gilt keine zeitliche Einschränkung – Gäste können das ganze Jahr buchen
          (sofern Kapazität frei ist). Mehrere Saisons können sich überlappen; es reicht, wenn
          mindestens eine Saison den gewünschten Zeitraum abdeckt. Bis ist der letzte offene Tag:
          An diesem Tag ist nur die Abreise möglich.
        </OnlineBookingFormHint>
        <OnlineBookingSeasonsEditor
          seasons={seasons}
          onChange={setSeasons}
          canWrite={canWrite}
        />
      </Card>
    );
  }

  if (subSection === "mindestaufenthalt") {
    return (
      <Card
        title="Mindestaufenthalt"
        description="Legt fest, wie viele Nächte Gäste online mindestens buchen müssen."
        bodyClassName="space-y-4"
      >
        <OnlineBookingFormHint>
          Beispiel: In der Hauptsaison mindestens 7 Nächte, außerhalb 2 Nächte. Ohne Zeiträume gilt
          nur der Standardwert für alle Buchungen.
        </OnlineBookingFormHint>
        <fieldset className="space-y-4" disabled={!canWrite}>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={minStay.enabled}
              onChange={(e) => setMinStay((m) => ({ ...m, enabled: e.target.checked }))}
            />
            Mindestaufenthalt für Online-Buchung aktivieren
          </label>
          {minStay.enabled ? (
            <>
              <label className="flex max-w-xs flex-col gap-1 text-sm">
                Standard Mindestnächte
                <span className="text-xs text-label-secondary">
                  Gilt außerhalb definierter Zeiträume und wenn keine Zeiträume angelegt sind.
                </span>
                <input
                  type="number"
                  className="eh-input w-28"
                  min={1}
                  max={365}
                  value={minStay.default_min_nights}
                  onChange={(e) =>
                    setMinStay((m) => ({
                      ...m,
                      default_min_nights: Math.max(1, Number(e.target.value) || 1),
                    }))
                  }
                />
              </label>
              <OnlineBookingMinStayPeriodsEditor
                periods={minStay.periods}
                onChange={(periods) => setMinStay((m) => ({ ...m, periods }))}
              />
            </>
          ) : null}
        </fieldset>
      </Card>
    );
  }

  return (
    <Card
      title="Same-Day-Cutoff"
      description="Ab welcher Uhrzeit Gäste am Anreisetag nicht mehr für heute online buchen können."
      bodyClassName="space-y-4"
    >
      <OnlineBookingFormHint>
        Beispiel: Ab 14:00 Uhr ist eine Anreise am selben Tag nicht mehr buchbar. Gäste können
        dann frühestens ab morgen anreisen.
      </OnlineBookingFormHint>
      <fieldset className="space-y-4" disabled={!canWrite}>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sameDayCutoff.enabled}
            onChange={(e) => setSameDayCutoff((c) => ({ ...c, enabled: e.target.checked }))}
          />
          Same-Day-Buchungssperre aktivieren
        </label>
        {sameDayCutoff.enabled ? (
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Cutoff-Uhrzeit
            <input
              type="time"
              className="eh-input"
              value={sameDayCutoff.cutoff_time}
              onChange={(e) => setSameDayCutoff((c) => ({ ...c, cutoff_time: e.target.value }))}
              required
            />
            <span className="text-xs text-label-secondary">
              Ab dieser Uhrzeit am heutigen Tag ist eine Anreise am selben Tag nicht mehr möglich.
            </span>
          </label>
        ) : null}
      </fieldset>
    </Card>
  );
}
